import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
import { IVimeoPlayerProgressEvent, VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { Subscription } from 'rxjs/internal/Subscription';
import { CustomCursorComponent } from '../../../../../ui/display/custom-cursor/custom-cursor.component';
import { LocalVimeoPlayerComponent } from '../../../../../ui/display/media/vimeo-player.component';
import { VideoControlSoundComponent } from './video-control-sound.component';
import config from './video-layout.config';
import { PageComponentVideoLayoutData } from './video-layout.interface';

@Component({
    selector: 'page-component-video-layout',
    templateUrl: './video-layout.html',
    styleUrl: './video-layout.scss',
    standalone: true,
    imports: [
        CommonModule,
        CustomCursorComponent,
        DfImageDirective,
        HrefComponent,
        IfSizeDirective,
        LocalVimeoPlayerComponent,
        ShellComponent,
        VideoControlSoundComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<PageComponentVideoLayoutComponent>(config)
export class PageComponentVideoLayoutComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit, OnDestroy {
    @ICmsContent({
        type: 'component'
    })
    component!: PageComponentVideoLayoutData;

    private _plyerSubscription?: Subscription;
    private _lastPlayerProgressPercent?: number;

    @ViewChild(VimeoPlayerComponent)
    set player(value: VimeoPlayerComponent) {
        if (value !== this._player) {
            this._player = value;

            this._plyerSubscription?.unsubscribe();

            this._plyerSubscription = this.player.playerChange.subscribe(() => this.detectChanges());
        }
    }

    get player(): VimeoPlayerComponent {
        return this._player;
    }

    private _player?: VimeoPlayerComponent;

    protected elementRef = inject(ElementRef);

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        this._plyerSubscription?.unsubscribe();
    }

    onCursorClick() {
        if (this.player) {
            if (this.player.isPlaying) {
                this.player.pause();
            } else {
                this.player.play();
            }
        }
    }

    handlePlayerPlay(_event: VimeoPlayerComponent): void {
        this.player.playerInstance.off('timeupdate', this.playerProgress);
        this.player.playerInstance.on('timeupdate', this.playerProgress);
    }

    handlePlayerPause(_event: VimeoPlayerComponent): void {
        this.player.playerInstance.off('timeupdate', this.playerProgress);
    }

    handlePlayerStop(_event: VimeoPlayerComponent): void {
        this.player.playerInstance.off('timeupdate', this.playerProgress);
    }

    handleVideoEnd(): void {
        if (this.player && !this.component.video_loop) {
            this._lastPlayerProgressPercent = undefined;

            this.player.stop();
        }
    }

    protected playerProgress = (event: IVimeoPlayerProgressEvent): void => {
        if (this._lastPlayerProgressPercent && event.percent < this._lastPlayerProgressPercent) {
            this.handleVideoEnd();
        } else {
            this._lastPlayerProgressPercent = event.percent;
        }
    };
}
